import isEmpty from "lodash.isempty";
import { prettifyFileExtension } from "tools/file/prettifyFileExtension";
import { compressImage } from "tools/file/compressImage";
import { AttachmentFile, RawAttachmentFile } from "ts/api/attachmentFile";
import { DebouncedFunc } from "lodash";
import firebase, {
  DocumentData as Doc,
  DocumentReference,
  storage,
  UploadTask,
} from "./firebase";

type DeleteFilesParams = { fileUrls: string[] };
type PathPair = [string, string?];

type OnProgressCallback = DebouncedFunc<
  (value: Partial<FileUploadState>) => void
>;
export const MAX_FILE_PREVIEW_SIZE = 100;

const db = firebase.firestore();
const storageRef = storage.ref();

export const getDocumentReference = <T>(
  pathPairs: PathPair[]
): DocumentReference<T> => {
  let ref: firebase.firestore.DocumentReference | null = null;
  pathPairs.forEach(([collection, docId]) => {
    if (ref) {
      ref = ref.collection(collection).doc(docId);
    } else {
      ref = db.collection(collection).doc(docId);
    }
  });
  return (ref as unknown) as DocumentReference<T>;
};
export const getRefs = <T = unknown>(collections: PathPair[], docs: Doc[]) => {
  const ref = getDocumentReference<T>(collections);
  return docs.map((doc) => ref.parent.doc(doc.id));
};

export type FileUploadState = {
  progress: number;
  isError?: boolean;
  isSuccess?: boolean;
  bytesTransferred?: number;
  totalBytes?: number;
};
export const addFiles = async (
  {
    files,
    path,
    useTitleAsName,
    compressMaxSizeMB,
  }: {
    files: RawAttachmentFile[];
    path: string;
    useTitleAsName?: boolean;
    /**
     * When set, raster images are compressed to this budget before upload
     * (see `tools/file/compressImage`). Left undefined the file is stored
     * exactly as provided — which is what Task Attachments and Task
     * submissions require.
     */
    compressMaxSizeMB?: number;
  },
  onProgress?: (
    id: string,
    uploadTask: UploadTask
  ) => OnProgressCallback | undefined
): Promise<AttachmentFile[]> => {
  if (isEmpty(files)) {
    return [];
  }

  const filesPromises = files.map(
    async (fileObject): Promise<AttachmentFile> => {
      try {
        const { id, title } = fileObject;
        // Compression can change the format (an opaque PNG becomes JPEG), so
        // the extension is derived from the file that is actually uploaded.
        const file =
          compressMaxSizeMB !== undefined && fileObject.file
            ? await compressImage(fileObject.file, compressMaxSizeMB)
            : fileObject.file;
        if (file && file.lastModified) {
          const ext = prettifyFileExtension(file);
          const fileName = `${path}/${useTitleAsName ? title : id}.${ext}`;
          const fileRef = storageRef.child(fileName);
          await new Promise<void>((res, rej) => {
            const uploadTask = fileRef.put(file);
            const handleProgress = onProgress?.(id, uploadTask);
            uploadTask.on(
              "state_changed",
              (snapshot) => {
                if (!handleProgress) {
                  return;
                }
                const { totalBytes, bytesTransferred } = snapshot;
                const progress = Math.round(
                  (bytesTransferred / totalBytes) * 100
                );
                handleProgress({ progress, totalBytes, bytesTransferred });
              },
              (error) => {
                if (!handleProgress) {
                  return;
                }
                handleProgress({ isError: true });
                handleProgress.flush();
                rej(error);
              },
              () => {
                if (handleProgress) {
                  handleProgress({ isSuccess: true, isError: false });
                  handleProgress.flush();
                }
                res();
              }
            );
          });
          const url: string = await fileRef.getDownloadURL();
          return {
            id,
            title,
            url,
            file: {
              name: file.name,
              size: file.size,
            },
            path: fileName,
          };
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return fileObject;
      } catch (error) {
        throw new Error(`Error while uploading file to storage: ${error}`);
      }
    }
  );
  const downloadFiles = await Promise.all(filesPromises);

  return downloadFiles;
};

export const deleteFiles = async (args: DeleteFilesParams) => {
  const { fileUrls } = args;
  if (isEmpty(fileUrls)) {
    return [];
  }

  const filesPromises = fileUrls.map(async (fileUrl) => {
    try {
      const fileRef = storage.refFromURL(fileUrl);

      await fileRef.delete();
    } catch (error) {
      console.error(`Error while deleting file from storage: ${error}`);
    }
  });
  await Promise.all(filesPromises);
};
